// ABOUTME: LemonSqueezy SDK initialization and checkout overlay utilities
// ABOUTME: Provides functions to initialize the Lemon.js SDK and open checkout overlays for subscription payments

/**
 * Initialize the LemonSqueezy SDK
 * Should be called once when the app loads
 */
export function initializeLemonSqueezy(): void {
  if (typeof window !== 'undefined' && !isLemonSqueezyLoaded()) {
    try {
      window.createLemonSqueezy?.();
    } catch (error) {
      console.error('Failed to initialize LemonSqueezy SDK:', error);
    }
  }
}

/**
 * Check if LemonSqueezy SDK is loaded and ready
 */
export function isLemonSqueezyLoaded(): boolean {
  return typeof window !== 'undefined' &&
         typeof window.LemonSqueezy !== 'undefined' &&
         typeof window.LemonSqueezy?.Url?.Open === 'function';
}

/**
 * Open a LemonSqueezy checkout overlay
 * @param checkoutUrl - The checkout URL returned from the API
 * @param onEvent - Callback when overlay events occur (optional)
 */
export function openCheckoutOverlay(
  checkoutUrl: string,
  onEvent?: (event: string) => void
): void {
  if (!isLemonSqueezyLoaded()) {
    throw new Error('LemonSqueezy SDK is not loaded. Call initializeLemonSqueezy() first.');
  }

  try {
    // Set up event handler BEFORE opening overlay (if callback provided)
    if (onEvent) {
      window.LemonSqueezy!.Setup({
        eventHandler: (event: string) => {
          // Pass event type to callback
          if (event === 'Checkout.Success' || event === 'Checkout.Closed') {
            onEvent(event);
          }
        }
      });
    }

    // Open the checkout overlay
    window.LemonSqueezy!.Url.Open(checkoutUrl);
  } catch (error) {
    console.error('Failed to open LemonSqueezy checkout:', error);
    throw error;
  }
}

/**
 * Type definitions for LemonSqueezy global objects
 */
declare global {
  interface Window {
    createLemonSqueezy?: () => void;
    LemonSqueezy?: {
      Url: {
        Open: (url: string) => void;
      };
      Setup: (config: { eventHandler: (event: string) => void }) => void;
    };
  }
}
